import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, FormControlLabel, Switch, FormControl, FormLabel, RadioGroup, Radio } from "@mui/material";
import { useState, useEffect } from "react";
import { Address } from "@/types/address.types";
import { addressFormSchema, AddressFormData } from "@/schemas/address.schema";

interface AddressFormDialogProps {
  open: boolean;
  mode: "add" | "edit";
  address?: Address;
  initialCall?: string;
  onClose: () => void;
  onSubmit: (data: AddressFormData) => Promise<void>;
}

export default function AddressFormDialog({
  open,
  mode,
  address,
  initialCall,
  onClose,
  onSubmit,
}: AddressFormDialogProps): JSX.Element {
  const [formData, setFormData] = useState<AddressFormData>({
    call: "",
    address: "",
    zip: "",
    outsea: false,
    status: "valid",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof AddressFormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && mode === "edit" && address) {
      setFormData({
        call: address.call,
        address: address.address,
        zip: address.zip,
        outsea: address.outsea || false,
        status: address.status,
      });
      setErrors({});
    } else if (open && mode === "add") {
      setFormData({
        call: initialCall || "",
        address: "",
        zip: "",
        outsea: false,
        status: "valid",
      });
      setErrors({});
    }
  }, [open, mode, address, initialCall]);

  const handleChange = (field: keyof AddressFormData, value: string | boolean): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    try {
      addressFormSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      const zodError = error as { issues?: Array<{ path: (string | number)[]; message: string }> };
      if (zodError.issues) {
        const newErrors: Partial<Record<keyof AddressFormData, string>> = {};
        zodError.issues.forEach((issue) => {
          if (issue.path.length > 0) {
            const field = issue.path[0] as keyof AddressFormData;
            newErrors[field] = issue.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("表单提交失败:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "add" ? "添加地址" : "编辑地址"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="呼号"
            fullWidth
            value={formData.call}
            onChange={(e) => handleChange("call", e.target.value)}
            error={!!errors.call}
            helperText={errors.call}
            disabled={submitting}
            slotProps={{
              htmlInput: {
                onInput: (e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement;
                  target.value = target.value.replace(/[^A-Za-z0-9/]/g, '').toUpperCase();
                }
              }
            }}
          />
          <TextField
            label="地址"
            fullWidth
            multiline
            rows={3}
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
            error={!!errors.address}
            helperText={errors.address}
            disabled={submitting}
          />
          <TextField
            label="邮编"
            fullWidth
            value={formData.zip}
            onChange={(e) => handleChange("zip", e.target.value)}
            error={!!errors.zip}
            helperText={errors.zip || (formData.outsea ? "海外地址邮编可选" : "国内地址邮编必填")}
            disabled={submitting}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 10,
                onInput: (e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement;
                  target.value = target.value.replace(/\D/g, '').slice(0, 10);
                }
              }
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.outsea}
                onChange={(e) => handleChange("outsea", e.target.checked)}
                disabled={submitting}
              />
            }
            label="海外地址"
          />
          {mode === "edit" && (
            <FormControl component="fieldset">
              <FormLabel component="legend">状态</FormLabel>
              <RadioGroup
                row
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
              >
                <FormControlLabel value="valid" control={<Radio />} label="有效" disabled={submitting} />
                <FormControlLabel value="invalid" control={<Radio />} label="无效" disabled={submitting} />
              </RadioGroup>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          取消
        </Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={submitting}>
          确认
        </Button>
      </DialogActions>
    </Dialog>
  );
}
